import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnChanges } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatHint } from '@angular/material/form-field';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { takeUntil } from 'rxjs';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';

@Component({
  // Was 'formly-field-custom-multicheckbox' — a copy-paste leftover that CustomMultiCheckboxComponent
  // (multi-checkbox.component.ts) also declares. Two standalone components on one selector match the
  // same node whenever a template imports both, so the tag rendered whichever the importer happened
  // to pull in. Formly resolves this component by its registered type key ('custom-toggle',
  // formly-fields/index.ts), never by selector, so renaming is invisible to every real call site.
  selector: 'formly-field-custom-toggle',
  template: `
    <div class="mb-4">
      <mat-slide-toggle
        class="w-full"
        labelPosition="before"
        hideIcon
        [required]="field.props.required"
        [aria-describedby]="props.description ? descriptionId : null"
        [formControl]="subField"
        [formlyDataCy]="field"
        >{{ props.label }} {{ field.props.required ? '*' : '' }}</mat-slide-toggle
      >
      @if (props.description) {
        <!-- No horizontal padding: the helper text aligns with the toggle label start (Figma). -->
        <!-- The id is what makes the hint reachable: mat-hint outside a mat-form-field is just a
             styled span, so without the aria-describedby binding above a screen reader announces
             only "<label>, switch" and drops the explanation entirely. -->
        <mat-hint [id]="descriptionId" class="inline-block leading-tight">{{ props.description }}</mat-hint>
      }
    </div>
  `,
  // `:host` is load-bearing, not decoration: a top-level ::ng-deep block emits these rules
  // UNSCOPED (no _nghost attribute), so for as long as ONE toggle field was alive anywhere they
  // applied to every .mdc-form-field in the app — mat-checkbox and mat-radio included. (Angular
  // ref-counts component styles and drops them once the last instance is destroyed —
  // REMOVE_STYLES_ON_COMPONENT_DESTROY defaults to true — so the leak was page-state-dependent
  // rather than permanent, which is exactly what made it hard to spot.) Scoping to the host keeps
  // the full-width label local to the toggle field. Consumers that need a full-width Material
  // label of their own must now say so themselves; see shifts-list.ts for the one site that did.
  styles: `
    :host ::ng-deep {
      .mdc-form-field {
        width: 100%;
        display: flex;
        flex: auto;
      }

      .mdc-label {
        width: 100%;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, ReactiveFormsModule, MatSlideToggle, MatHint, FormlyDataCyDirective],
})
export class CustomToggleComponent extends FieldType implements OnInit, OnChanges {
  private destroy$ = injectDestroy();
  subField = this.fb.control(false);
  /** Unique per instance so multiple toggles on one page each describe their own hint. */
  readonly descriptionId = `dd-toggle-hint-${crypto.randomUUID()}`;
  constructor(
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.subField.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const stringValue = value ? 'true' : 'false';

      this.formControl.markAsDirty();
      this.formControl.markAsTouched();
      this.field.formControl.patchValue(stringValue, {
        emitEvent: true,
      });

      this.changeDetectorRef.markForCheck();
    });

    // Listen to changes from external sources (like API mapping)
    this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      // Update the toggle if the form control value changes externally
      if (value === true || value === 'true') {
        this.subField.patchValue(true, { emitEvent: false });
      } else if (value === false || value === 'false') {
        this.subField.patchValue(false, { emitEvent: false });
      }

      this.changeDetectorRef.markForCheck();
    });

    // Initial sync FROM the model, not a user edit — must not fire the subField.valueChanges
    // subscription above (it calls formControl.markAsDirty()), or every detail page with a
    // toggle field opens already dirty (#20844: the ● indicator lit without any user change).
    // Both spellings, because this component is not the only writer: it persists the STRINGS
    // 'true'/'false', while `defaultValue`, the config API and the form-generator all store real
    // booleans. Recognising only its own spelling made a stored `true` fall through to
    // `defaultValue` and render the switch OFF while the saved config said on (measured
    // 2026-09-16 on the "In overzicht" field flag, #21239 task 21250).
    if (this.model === undefined) {
      this.subField.patchValue(this.field.defaultValue, { emitEvent: false });
    } else if (this.model[this.key as string] === true || this.model[this.key as string] === 'true') {
      this.subField.patchValue(true, { emitEvent: false });
    } else if (this.model[this.key as string] === false || this.model[this.key as string] === 'false') {
      this.subField.patchValue(false, { emitEvent: false });
    } else if (this.field.defaultValue !== undefined) {
      this.subField.patchValue(this.field.defaultValue, { emitEvent: false });
    }
    this.disabledSubField();
  }

  ngOnChanges() {
    this.disabledSubField();
  }

  disabledSubField() {
    // enable()/disable() unconditionally call updateValueAndValidity() (even when the control is
    // already in that state), which emits through subField.valueChanges — the same pipe the
    // ngOnInit subscription above uses to mark the real formControl dirty. Every call site here
    // (ngOnInit, and ngOnChanges on every re-check) would otherwise dirty the field on its own,
    // same failure mode as #20844. { emitEvent: false } keeps enable/disable side-effect-free.
    if (this.field.formControl.disabled || this.form.disabled) {
      this.subField.disable({ emitEvent: false });
    } else {
      this.subField.enable({ emitEvent: false });
    }
  }
}
